import React, { useState, useEffect } from "react";
import {
  AppBar,
  CardMedia,
  Toolbar,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Drawer,
  List,
  ListItemButton,
  ListItemText,
  Box,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import Scale from "@mui/icons-material/Scale";

export type SystemType = "ANUNCIO360" | "ESCALA11";

interface Item {
  label: string;
  onClick: () => void;
}

interface NavItems {
  mainItems: Array<Item>;
  systemItem: Item | null;
}

interface NavbarProps {
  itens: NavItems;
  role: "MASTER" | "PARTNER" | "ANALYST";
  onLogout: () => void;
  system: SystemType;
  currentSelectionKey: string;
}

const SELECAO_TO_LABEL: { [key: string]: string } = {
  analise: "Anúncios",
  gerenciarCompeticao: "Competição",
};

const Navbar: React.FC<NavbarProps> = ({
  itens,
  role,
  onLogout,
  system,
  currentSelectionKey,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
  const [selectedItemLabel, setSelectedItemLabel] = useState<string>(
    SELECAO_TO_LABEL[currentSelectionKey] || "Anúncios"
  );

  useEffect(() => {
    const newLabel = SELECAO_TO_LABEL[currentSelectionKey];

    if (newLabel) {
      setSelectedItemLabel(newLabel);
    }
  }, [currentSelectionKey]);

  const roleMap = {
    MASTER: "Master",
    PARTNER: "Parceiro",
    ANALYST: "Analista",
  };

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };

  const handleItemClick = (item: Item) => {
    setSelectedItemLabel(item.label);
    item.onClick();
  };

  const { mainItems, systemItem } = itens;

  // Determina o título e o logo com base no sistema
  const systemConfig = {
    ANUNCIO360: {
      title: `Anúncio 360 - Painel ${roleMap[role]}`,
      logoPath: "/anuncio360-icon.png",
      logoAlt: "Logo Anúncio360",
      appBarColor: "#215376",
      selectedBgColor: "rgba(24, 65, 94, 1)",
      corPadrao: "#215376",
      tamanho: 28,
    },
    ESCALA11: {
      title: `Escala 11 - Painel ${roleMap[role]}`,
      logoPath: "/Marca-OffWhite.svg",
      logoAlt: "Logo Escala 11",
      appBarColor: "#008870",
      selectedBgColor: "#005848ff",
      corPadrao: "#008870",
      tamanho: 32,
    },
  };

  const currentConfig = systemConfig[system];

  return (
    <Box sx={{ display: "flex" }}>
      <AppBar
        position="sticky"
        sx={{ backgroundColor: currentConfig.corPadrao }}
      >
        <Toolbar>
          {/* Ícone de Menu para abrir o Drawer */}
          {isMobile ? (
            <IconButton
              edge="start"
              color="inherit"
              aria-label="menu"
              sx={{ mr: 2 }}
              onClick={handleDrawerToggle}
            >
              <MenuIcon />
            </IconButton>
          ) : null}

          {/* Título da barra de navegação (LADO ESQUERDO) */}
          <Typography
            variant="h6"
            sx={{
              display: "flex",
              gap: 1,
              alignItems: "center",
              boxSizing: "border-box",
              // Mantém o título à esquerda
            }}
          >
            <CardMedia
              component="img"
              image={currentConfig.logoPath}
              alt={currentConfig.logoAlt}
              color={currentConfig.corPadrao}
              sx={{
                width: "auto",
                height: currentConfig.tamanho,
                display: "inline-block",
                marginRight: "2px",
              }}
            />
            {currentConfig.title} {/* Título dinâmico */}
          </Typography>

          {/* Menu principal (CENTRO) */}
          {!isMobile ? (
            <Box
              sx={{
                display: "flex",
                flexGrow: 1, // Faz com que ocupe o espaço restante
                height: "100%",
                justifyContent: "center", // Centraliza os itens principais
                mx: 2, // Margem horizontal para separar do título e do item de sistema
              }}
            >
              {mainItems.map((item, index) => {
                // Renderiza mainItems
                const isSorteio = item.label.toLowerCase() === "sorteio";
                const isSelected = item.label === selectedItemLabel;

                return (
                  <Typography
                    key={index}
                    onClick={() => handleItemClick(item)}
                    sx={{
                      color: isSorteio ? currentConfig.corPadrao : "white",
                      backgroundColor: isSorteio
                        ? "white"
                        : currentConfig.corPadrao,
                      border: "none",
                      boxShadow: isSorteio
                        ? "none, 0px 4px 5px 0px rgba(0, 0, 0, 0.14), none;"
                        : "none",
                      cursor: "pointer",
                      minWidth: "auto",
                      padding: "4px 16px",
                      textAlign: "center",
                      alignContent: "center",
                      transition: "all 0.3s ease",
                      ...(isSelected && {
                        backgroundColor: isSorteio
                          ? "rgba(235,235,235,1)"
                          : currentConfig.selectedBgColor,
                        color: isSorteio ? currentConfig.corPadrao : "white",
                        borderRadius: "0px 0px 10px 10px",
                        fontWeight: "bold",
                        transform: "scaleY(1.1)",
                      }),
                    }}
                  >
                    {item.label}
                  </Typography>
                );
              })}
            </Box>
          ) : null}

          {/* Item de Sistema e Logout (LADO DIREITO) */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1, // Espaço entre o item de sistema e o botão de logout
            }}
          >
            {!isMobile && systemItem ? ( // Renderiza o item de sistema apenas em desktop, se existir
              <Typography
                onClick={() => handleItemClick(systemItem)}
                sx={{
                  color: "white",
                  backgroundColor: "rgba(0, 0, 0, 0.2)", // Um fundo leve para destacá-lo
                  borderRadius: "4px",
                  cursor: "pointer",
                  padding: "4px 8px",
                  transition: "background-color 0.3s ease",
                  "&:hover": {
                    backgroundColor: "rgba(0, 0, 0, 0.4)",
                  },
                }}
              >
                {systemItem.label}
              </Typography>
            ) : null}

            {/* Ícone de Logout */}
            <IconButton color="inherit" onClick={onLogout} sx={{ ml: 0 }}>
              <ExitToAppIcon />
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Drawer (menu lateral) para telas pequenas: inclui TUDO */}
      <Drawer
        sx={{
          width: 240,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: 240,
            boxSizing: "border-box",
          },
        }}
        anchor="left"
        open={drawerOpen}
        onClose={handleDrawerToggle}
      >
        <List>
          {/* Main Items no Drawer */}
          {mainItems.map((item, index) => (
            <ListItemButton
              key={`main-${index}`}
              onClick={() => {
                handleItemClick(item);
                handleDrawerToggle();
              }}
            >
              <ListItemText primary={item.label} />
            </ListItemButton>
          ))}

          {/* System Item no Drawer */}
          {systemItem && (
            <ListItemButton
              key={`system`}
              onClick={() => {
                handleItemClick(systemItem);
                handleDrawerToggle();
              }}
              sx={{ borderTop: "1px solid #eee", mt: 1, pt: 1 }} // Separador visual
            >
              <ListItemText primary={systemItem.label} />
            </ListItemButton>
          )}
        </List>
      </Drawer>
    </Box>
  );
};

export default Navbar;
